import useSWR from "swr";
import { useCallback } from "react";
import { NOOP, flattenedQueryParams } from "./query-parser";
import { buildUrl } from "./build-url";
import { fetcher } from "./fetcher";
/**
 * A hook that provides an interface for making API requests.
 *
 * @param {UseApiWithOptions<Q>} options - An object containing the following properties:
 *   - endpoint: The API endpoint to make the request to.
 *   - endpointParams: An object containing any parameters to be included in the endpoint URL.
 *   - queryParams: An object containing any query parameters to be included in the request URL.
 *   - shouldMakeRequest: A boolean indicating whether the request should be made.
 *   - swrOptions: An object containing options for the SWR hook.
 * @return {useAPIResponseInterface<T>} An object containing the SWR hook response, as well as the "revalidate" function and the "key" value.
 */
export const useAPI = ({ endpoint, endpointParams = {}, queryParams, shouldMakeRequest = true, swrOptions = {}, }) => {
    const queryParamsValue = Object.values(queryParams || NOOP);
    const endpointParamsValue = endpointParams
        ? Object.values(endpointParams)
        : [];
    const hasAllParams = ![...queryParamsValue, ...endpointParamsValue].includes(null);
    const endpointParamsExact = Object.keys(endpointParams).reduce((fold, k) => {
        if (endpointParams[k] == null) {
            return fold;
        }
        return {
            ...fold,
            [k]: endpointParams[k],
        };
    }, {});
    const key = shouldMakeRequest && hasAllParams
        ? buildUrl({
            endpoint,
            endpointParams: endpointParamsExact,
            queryParams: flattenedQueryParams(queryParams),
        })
        : undefined;
    const swrHook = useSWR(
    // When the `key` passed into `useSWR` is `null`, SWR will not make a web request.
    // See SWR docs: https://swr.vercel.app/docs/conditional-fetching#conditional
    key || null, fetcher, swrOptions);
    const revalidateDeprecated = useCallback(async () => swrHook
        .mutate()
        .then(() => true)
        .catch(() => false), [swrHook.mutate]);
    return { ...swrHook, revalidate: revalidateDeprecated, key };
};
